import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const HeroSlider = ({ companies, activeIndex, setActiveIndex }) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % companies.length);
  }, [companies.length, setActiveIndex]);

  const goToPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + companies.length) % companies.length);
  }, [companies.length, setActiveIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrev]);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) goToNext();
    if (distance < -minSwipeDistance) goToPrev();
  };

  const currentCompany = companies[activeIndex];

  return (
    <section
      data-testid="hero-slider"
      className="relative h-screen w-full overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Background Images */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`bg-${currentCompany.slug}`}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0"
        >
          <img
            src={currentCompany.image}
            alt={currentCompany.name}
            className="w-full h-full object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/10 to-obsidian" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-28 sm:pb-32 md:pb-40 px-4 sm:px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={`content-${currentCompany.slug}`}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-3xl text-left"
          >
            {/* Company Tag */}
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="w-10 sm:w-12 h-[2px] bg-gold" />
              <span className="text-white text-[10px] sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] font-medium">
                {currentCompany.slug === 'builder' && "Ananthameenakshi & Sriram Associates"}
                {currentCompany.slug === 'developers' && 'Ananthameenakshi Developers'}
                {currentCompany.slug === 'architecture' && 'Ananthameenakshi Architects'}
                {currentCompany.slug === 'materials' && 'Ananthameenakshi & Meenakshi Agency'}
              </span>
            </div>

            {/* Title */}
            <h1 
              className="font-heading text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-3 sm:mb-4 leading-[1.1] tracking-tight text-left"
              data-testid="hero-title"
            >
              {currentCompany.tagline}
            </h1>

            {/* Description */}
            <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 max-w-md sm:max-w-xl leading-relaxed text-left">
              {currentCompany.description}
            </p>

            {/* CTA Button */}
            <button
              onClick={() => document.getElementById("projects")?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base "
              data-testid="hero-cta" >              
             {currentCompany.slug === "materials" ? "View Products" : "View Projects"}
              <ChevronRight size={18} />
            </button>
          </motion.div>
        </AnimatePresence>

        {/* Slide Indicators - Bottom */}
        <div className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3" data-testid="slide-indicators">
          {companies.map((company, index) => (
            <button
              key={company.id}
              onClick={() => setActiveIndex(index)}
              className={`swipe-dot ${index === activeIndex ? 'active' : ''}`}
              data-testid={`slide-dot-${index}`}
              aria-label={`Go to ${company.name}`}
            />
          ))}
        </div>

        {/* Arrow Navigation - Center Left & Right 
        <button
          onClick={goToPrev}
          className="hidden rounded-full md:flex absolute left-0 top-1/2 -translate-y-1/2 w-14 h-14 border border-white/20 items-center justify-center text-white hover:bg-white/10 hover:border-gold transition-colors z-20"
          data-testid="prev-arrow"
          aria-label="Previous slide"
        >
          <ChevronLeft size={28} />
        </button>
        <button
          onClick={goToNext}
          className="hidden rounded-full md:flex absolute right-0 top-1/2 -translate-y-1/2 w-14 h-14 border border-white/20 items-center justify-center text-white hover:bg-white/10 hover:border-gold transition-colors z-20"
          data-testid="next-arrow"
          aria-label="Next slide"
        >
          <ChevronRight size={28} />
        </button>
*/}
        {/* Company Selector Pills */}
        <div className=" absolute top-20 sm:top-24  md:top-28 left-4 sm:left-6 md:left-12 lg:left-20 flex items-center justify-center flex-wrap gap-1.5 sm:gap-2" data-testid="company-selector">
          {companies.map((company, index) => (
            <button
              key={company.id}
              onClick={() => setActiveIndex(index)}
              className={`  rounded-1xl px-2.5 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs uppercase tracking-wider font-medium transition-colors ${
                index === activeIndex
                  ? ' bg-primary text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-black/50 text-gold transition-colors'
              }`}
              data-testid={`company-tab-${company.slug}`}
            >
              {company.id.replace('Triad ', '')}
            </button>
          ))}
{/*hint button  */} 
    
          <div className="relative group inline-block flex flex-wrap">
            <button
              className="hidden sm:block lg:ml-2 text-sm w-8 h-8 rounded-full p-2 bg-white/10 hover:bg-black/50 text-gray-300 transition-colors ml-8"
              aria-label="Hint"
              data-testid="hint-button"
             >
              ?
            </button>
            {/* Tooltip */}     
                 <span className="hidden sm:block absolute bottom-full left-1/2 mb-2 w-56 -translate-x-1/2 rounded-md bg-black text-white text-xs px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                   Select the service you need and scroll down to view each company.
                  <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black"></span>
               </span>
          </div>
        </div>
      </div>

      {/* Swipe Hint (Mobile) */}
      <div className="md:hidden absolute bottom-16 left-1/2 -translate-x-1/2 text-gray-400 text-[10px] uppercase tracking-wider animate-pulse">
        Swipe to explore
      </div>
    </section>
  );
};

export default HeroSlider;
